import { Role, User } from '@dc-shared-web/hooks/api/user'

type permissionQuery = {
  resource_uuid?: string
  resource_type?: string
  role?: string
}

/**
 * Checks if a User has an explicit permission entry based on specified criteria within a permissionQuery.
 * This function does not evaluate hierarchical permissions; it only checks for direct permissions entries.
 * For example, if a user is a Super Admin and the function is used to check for Team Member privileges on a team,
 * it will return false unless the Team Member privilege is explicitly defined for the user.
 *
 * @param {User} user - The user whose permissions are being checked.
 * @param {permissionQuery} query - The query containing criteria to filter the permissions. This can include
 *                                  'resource_type', 'resource_uuid', and 'role'. Each is optional, but used to
 *                                  filter the permissions specifically if provided.
 * @returns {boolean} - Returns true if the user has at least one permission entry that matches all provided criteria,
 *                      otherwise returns false.
 */
export function userHasExplicitPermission(
  user: User,
  query: permissionQuery
): boolean {
  let permissions = user.permissions

  if (query.resource_type) {
    permissions = permissions.filter(
      (permission) => permission.resource_type === query.resource_type
    )
  }

  if (query.resource_uuid) {
    permissions = permissions.filter(
      (permission) => permission.resource_uuid === query.resource_uuid
    )
  }

  if (query.role) {
    permissions = permissions.filter(
      (permission) => permission.role === query.role
    )
  }

  return permissions.length > 0
}

export const getTeamUuidFromId = (
  teamId: number,
  user: User
): string | null => {
  return (
    user.teams.find((office) => office.id.toString() === teamId.toString())
      ?.id ?? null
  )
}

export function getUserRoleForTeam(user: User, teamUuid: string): Role {
  if (user && user.is_super_admin) {
    return Role.ROLE_SUPER_ADMIN
  }
  return user?.roles?.filter((role) => role.team_uuid === teamUuid)[0]
    ?.role as Role
}

export function getTeamAdminTeamIds(user: User): string[] {
  return user.teams.filter((team) => team.is_admin).map((team) => team.id)
}

export function isUserSuperAdminAndInHQDoc(
  user: User,
  teamId?: number | null
): boolean {
  if (!teamId) {
    return false
  }
  return user.is_super_admin && teamId === 1
}

export function hasMovePrivileges({
  user,
  document,
}: {
  user: User
  document: { teamId: number; teamUUID: string }
}): boolean {
  const isSuperAdminAndInIsHQDoc = isUserSuperAdminAndInHQDoc(
    user,
    document.teamId
  )

  return isSuperAdminAndInIsHQDoc || !user.is_super_admin
}
